// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Utility functions for timezone conversion and display formatting.
 *
 * All timestamps are stored in UTC. Display functions format to the
 * browser's local timezone and append the timezone abbreviation so
 * the user always knows which timezone they're looking at.
 */

/**
 * Convert any timestamp to UTC ISO string
 * @param {string|Date|number} timestamp - The timestamp to convert (can be in various formats)
 * @returns {string} - UTC ISO string
 */
export function convertToUTC(timestamp) {
  if (!timestamp) {
    return new Date().toISOString();
  }

  try {
    let date;

    // Handle various timestamp formats
    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      // Determine if timestamp is in seconds or milliseconds using magnitude check.
      // Unix timestamps in seconds for years 2001-2286 are in the range ~10^9 to ~10^10.
      // Unix timestamps in milliseconds for the same range are ~10^12 to ~10^13.
      // Using 10^12 as the threshold handles both integer and float timestamps correctly.
      // (The old string-length check failed for float timestamps like 1770388015.0901542)
      if (timestamp < 1e12) {
        // Timestamp is in seconds (possibly with fractional part) - convert to ms
        date = new Date(Math.floor(timestamp * 1000));
      } else {
        // Timestamp is already in milliseconds
        date = new Date(timestamp);
      }
    } else if (typeof timestamp === 'string') {
      // Try to parse the string
      // Handle common formats including those with timezone info
      date = new Date(timestamp);

      // If invalid date, try some common formats
      if (isNaN(date.getTime())) {
        // Try parsing as Unix timestamp string
        const numTimestamp = parseInt(timestamp);
        if (!isNaN(numTimestamp)) {
          return convertToUTC(numTimestamp);
        }

        // If still invalid, return current time in UTC
        console.warn('Unable to parse timestamp:', timestamp);
        return new Date().toISOString();
      }
    } else {
      // Unknown format, use current time
      console.warn('Unknown timestamp format:', timestamp);
      return new Date().toISOString();
    }

    // Convert to UTC ISO string
    return date.toISOString();
  } catch (error) {
    console.error('Error converting timestamp to UTC:', error, timestamp);
    return new Date().toISOString();
  }
}

// ─── Local timezone display helpers ──────────────────────────────────

/**
 * Returns the browser's local timezone abbreviation (e.g. "EST", "PST", "UTC").
 */
export function localTZ() {
  try {
    return (
      Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value || ''
    );
  } catch {
    return '';
  }
}

function _toDate(v) {
  if (v instanceof Date) return v;
  if (typeof v === 'number' && v < 1e12) return new Date(v * 1000);
  return new Date(v);
}

/**
 * Full date + time + TZ   →  "3/10/2026, 2:30:45 PM EST"
 */
export function fmtDateTime(value) {
  try {
    const d = _toDate(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleString([], { timeZoneName: 'short' });
  } catch {
    return String(value);
  }
}

/**
 * Time + seconds + TZ   →  "2:30:45 PM EST"
 */
export function fmtTime(value) {
  try {
    const d = _toDate(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleTimeString([], { timeZoneName: 'short' });
  } catch {
    return String(value);
  }
}

/**
 * Date-only (no TZ needed)   →  "3/10/2026"
 */
export function fmtDate(value) {
  try {
    const d = _toDate(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleDateString();
  } catch {
    return String(value);
  }
}

/**
 * Custom format with TZ appended.
 * @param {*} value - date-like value
 * @param {Intl.DateTimeFormatOptions} opts - toLocaleString options
 */
export function fmtWithTZ(value, opts = {}) {
  try {
    const d = _toDate(value);
    if (isNaN(d)) return String(value);
    return d.toLocaleString([], { ...opts, timeZoneName: 'short' });
  } catch {
    return String(value);
  }
}
